import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from './Footer';

export default function Layout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isRTL = i18n.language === 'ar';

  const toggleLang = () => {
    i18n.changeLanguage(isRTL ? 'en' : 'ar');
  };

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => { closeMenu(); }, [location.pathname, closeMenu]);

  useEffect(() => {
    if (menuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 400);
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/services', label: t('nav.services') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <>
      <header className={`header${scrolled ? ' header-scrolled' : ''}`}>
        <div className="container">
          <div className="nav-wrapper">
            <Link to="/" className="logo" onClick={closeMenu}>
              <span className="logo-icon">W</span>
              <div className="logo-text-group">
                <span className="logo-text">{t('brand')}</span>
                <span className="logo-subtitle">{t('brandEn')}</span>
              </div>
            </Link>

            <nav className={`nav-menu${menuOpen ? ' open' : ''}`}>
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`nav-link${location.pathname === link.to ? ' active' : ''}`}
                  onClick={closeMenu}
                >
                  {link.label}
                </Link>
              ))}
              <button className="lang-toggle mobile-only" onClick={toggleLang}>
                {t('langSwitch')}
              </button>
            </nav>

            <div className="nav-actions">
              <button className="lang-toggle desktop-only" onClick={toggleLang}>
                <i className="fas fa-globe" />
                {t('langSwitch')}
              </button>
              <Link to="/contact" className="btn btn-nav desktop-only">
                {t('nav.contact')}
              </Link>
              <button
                className={`mobile-menu-btn${menuOpen ? ' active' : ''}`}
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Menu"
                aria-expanded={menuOpen}
              >
                <span /><span /><span />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main>
        <Outlet />
      </main>

      <Footer />

      {/* Back to top */}
      <button
        className={`back-to-top${showBackToTop ? ' visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
      >
        <i className="fas fa-arrow-up" />
      </button>

      {/* WhatsApp */}
      <a
        href="https://wa.me/601139041634?text=Hello"
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
      >
        <i className="fab fa-whatsapp" />
        <span className="whatsapp-tooltip">{t('footer.whatsappMsg')}</span>
      </a>

      <style>{`
        .mobile-only { display: none; }
        @media (max-width: 768px) {
          .desktop-only { display: none; }
          .mobile-only { display: inline-block; margin-top: 10px; }
        }
      `}</style>
    </>
  );
}
